import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { tendApi } from '../api/tendApi';
import { AspectBucket, DEFAULT_UNIVERSAL_CHANNEL_ASPECT } from '../utils/channelAspect';
import { mergeSafeZones, SafeZoneMap } from '../utils/safeZones';

/**
 * Brand color palette with a universal layer beneath it.
 *
 * Resolution (same model as guidelines/fonts): the brand's own palette,
 * when NON-EMPTY, overrides the universal palette; an empty brand palette
 * inherits. Consumers read `colors` (resolved). Brand writes are
 * COPY-ON-WRITE: editing while inheriting first materializes the
 * universal colors as the brand's own (exactly how saving the guidelines
 * textarea creates the brand override from universal content).
 *
 * Pattern: FontLibraryContext-style provider + a module-level cache with
 * pub-sub, so the Brand dropdown panel and any open ColorPickerPopover stay
 * live-synced without prop drilling. One overlay-settings GET loads BOTH
 * layers. Universal writes (superadmin) notify every subscribed brand —
 * inheriting brands see the change immediately.
 *
 * Writes are serialized per brand through a promise chain — panel edits and
 * picker "save to palette" clicks can't clobber each other in-tab.
 */

/** Swatches per palette (brand and universal); the server caps at the same number. Was 6; three rows of six since QA bucket 1. */
export const MAX_PALETTE_COLORS = 18;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// ---------------------------------------------------------------------------
// Module-level cache + pub-sub
// ---------------------------------------------------------------------------
interface PaletteEntry {
  own: string[];
  universal: string[];
  /** Sparse per-channel aspect overrides ({} = fully inheriting universal). */
  channelAspect: Record<string, string>;
  /** Full per-platform universal aspect map. */
  universalChannelAspect: Record<string, string>;
  /** Universal safe zones (seeds merged with the stored value). Universal only. */
  universalSafeZones: SafeZoneMap;
}

const emptyEntry = (): PaletteEntry => ({
  own: [],
  universal: [],
  channelAspect: {},
  universalChannelAspect: { ...DEFAULT_UNIVERSAL_CHANNEL_ASPECT },
  universalSafeZones: mergeSafeZones(null),
});

const paletteCache = new Map<number, PaletteEntry>();
const listeners = new Map<number, Set<(entry: PaletteEntry) => void>>();
const loads = new Map<number, Promise<PaletteEntry>>();
const writeChains = new Map<number, Promise<unknown>>();
let universalWriteChain: Promise<unknown> = Promise.resolve();

const resolveColors = (entry: PaletteEntry): string[] =>
  entry.own.length > 0 ? entry.own : entry.universal;

const notify = (brandId: number, entry: PaletteEntry) => {
  paletteCache.set(brandId, entry);
  listeners.get(brandId)?.forEach((fn) => fn(entry));
};

/** Universal palette changed → every subscribed brand re-resolves. */
const notifyUniversal = (universal: string[]) => {
  paletteCache.forEach((entry, brandId) => {
    notify(brandId, { ...entry, universal });
  });
};

/** Universal aspect map changed → every subscribed brand re-resolves. */
const notifyUniversalAspect = (universalChannelAspect: Record<string, string>) => {
  paletteCache.forEach((entry, brandId) => {
    notify(brandId, { ...entry, universalChannelAspect });
  });
};

/** Universal safe zones changed → every subscribed brand redraws its bands. */
const notifyUniversalSafeZones = (universalSafeZones: SafeZoneMap) => {
  paletteCache.forEach((entry, brandId) => {
    notify(brandId, { ...entry, universalSafeZones });
  });
};

export const loadBrandPalette = (brandId: number, force = false): Promise<PaletteEntry> => {
  if (!force) {
    const cached = paletteCache.get(brandId);
    if (cached) return Promise.resolve(cached);
    const inflight = loads.get(brandId);
    if (inflight) return inflight;
  }
  const p = tendApi
    .getBrandOverlaySettings(brandId)
    .then((settings) => {
      const entry: PaletteEntry = {
        own: ((settings as any)?.color_palette as string[]) ?? [],
        universal: ((settings as any)?.universal_color_palette as string[]) ?? [],
        channelAspect: ((settings as any)?.channel_aspect as Record<string, string>) ?? {},
        universalChannelAspect:
          ((settings as any)?.universal_channel_aspect as Record<string, string>) ?? {
            ...DEFAULT_UNIVERSAL_CHANNEL_ASPECT,
          },
        universalSafeZones: mergeSafeZones((settings as any)?.universal_safe_zones),
      };
      notify(brandId, entry);
      return entry;
    })
    .catch(() => {
      return paletteCache.get(brandId) ?? emptyEntry();
    })
    .finally(() => {
      loads.delete(brandId);
    });
  loads.set(brandId, p);
  return p;
};

/** Serialize a brand-palette write; resolves true on success.
    `mutate` receives the RESOLVED colors (copy-on-write base). */
const queueWrite = (
  brandId: number,
  mutate: (resolved: string[]) => string[] | null
): Promise<boolean> => {
  const chain = (writeChains.get(brandId) ?? Promise.resolve()).then(async () => {
    const entry = paletteCache.get(brandId) ?? (await loadBrandPalette(brandId));
    const next = mutate(resolveColors(entry));
    if (next === null) return false; // no-op (e.g. already present / full)
    // updateBrandOverlaySettings returns null on failure (it never throws).
    const result = await tendApi.updateBrandOverlaySettings(brandId, {
      color_palette: next,
    } as any);
    if (!result) return false;
    notify(brandId, {
      ...entry,
      own: ((result as any).color_palette as string[]) ?? next,
      universal:
        ((result as any).universal_color_palette as string[]) ?? entry.universal,
      channelAspect:
        ((result as any).channel_aspect as Record<string, string>) ?? entry.channelAspect,
      universalChannelAspect:
        ((result as any).universal_channel_aspect as Record<string, string>) ??
        entry.universalChannelAspect,
    });
    return true;
  });
  writeChains.set(brandId, chain.catch(() => false));
  return chain;
};

export const saveBrandColorToPalette = (brandId: number, hex: string): Promise<boolean> => {
  const lc = hex.toLowerCase();
  if (!HEX_RE.test(lc)) return Promise.resolve(false);
  return queueWrite(brandId, (resolved) => {
    if (resolved.includes(lc) || resolved.length >= MAX_PALETTE_COLORS) return null;
    return [...resolved, lc];
  });
};

export const removeBrandColorFromPalette = (brandId: number, hex: string): Promise<boolean> => {
  const lc = hex.toLowerCase();
  return queueWrite(brandId, (resolved) =>
    resolved.includes(lc) ? resolved.filter((c) => c !== lc) : null
  );
};

export const setBrandPalette = (brandId: number, colors: string[]): Promise<boolean> =>
  queueWrite(brandId, () => colors.map((c) => c.toLowerCase()));

/** Superadmin: replace the universal palette. Null-return = failure. */
export const setUniversalPalette = (colors: string[]): Promise<boolean> => {
  const chain = universalWriteChain.then(async () => {
    const result = await tendApi.updateUniversalColorPalette(
      colors.map((c) => c.toLowerCase())
    );
    if (!result) return false;
    notifyUniversal(result.colors ?? []);
    return true;
  });
  universalWriteChain = chain.catch(() => false);
  return chain;
};

/** Replace the brand's sparse channel-aspect override map (whole-map PATCH;
    omit a platform to re-inherit universal). Serialized on the same per-brand
    chain as palette writes — both PATCH the same endpoint. */
export const setBrandChannelAspect = (
  brandId: number,
  next: Record<string, AspectBucket>
): Promise<boolean> => {
  const chain = (writeChains.get(brandId) ?? Promise.resolve()).then(async () => {
    const entry = paletteCache.get(brandId) ?? (await loadBrandPalette(brandId));
    const result = await tendApi.updateBrandOverlaySettings(brandId, {
      channel_aspect: next,
    } as any);
    if (!result) return false;
    notify(brandId, {
      ...entry,
      channelAspect:
        ((result as any).channel_aspect as Record<string, string>) ?? next,
      universalChannelAspect:
        ((result as any).universal_channel_aspect as Record<string, string>) ??
        entry.universalChannelAspect,
    });
    return true;
  });
  writeChains.set(brandId, chain.catch(() => false));
  return chain;
};

/** Superadmin: replace the universal channel-aspect map (complete map). */
export const setUniversalChannelAspectMap = (
  map: Record<string, AspectBucket>
): Promise<boolean> => {
  const chain = universalWriteChain.then(async () => {
    const result = await tendApi.updateUniversalChannelAspect(map);
    if (!result) return false;
    notifyUniversalAspect(result.channel_aspect ?? map);
    return true;
  });
  universalWriteChain = chain.catch(() => false);
  return chain;
};

/** Universal admin: sparse safe-zone write; the server answers with the merged map. */
export const setUniversalSafeZonesSparse = (
  sparse: Record<string, Record<string, unknown>>
): Promise<boolean> => {
  const chain = universalWriteChain.then(async () => {
    const result = await tendApi.updateUniversalSafeZones(sparse);
    if (!result) return false;
    notifyUniversalSafeZones(mergeSafeZones(result.safe_zones));
    return true;
  });
  universalWriteChain = chain.catch(() => false);
  return chain;
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
interface BrandPaletteValue {
  brandId: number | null;
  /** RESOLVED palette (own if non-empty, else universal).
      null = no provider / not loaded → consumers fall back to their own presets */
  colors: string[] | null;
  /** The brand's own layer ([] = inheriting universal). */
  ownColors: string[] | null;
  /** True when the brand has its own palette (overriding universal). */
  isOverride: boolean;
  /** The universal layer. */
  universalColors: string[] | null;
  /** Sparse per-channel aspect overrides ({} = fully inheriting). */
  channelAspect: Record<string, string> | null;
  /** Full per-platform universal aspect map. */
  universalChannelAspect: Record<string, string> | null;
  /** Universal safe zones (null = no provider / not loaded → seeds). */
  universalSafeZones: SafeZoneMap | null;
  isLoaded: boolean;
  saveColor: (hex: string) => Promise<boolean>;
  removeColor: (hex: string) => Promise<boolean>;
  setColors: (colors: string[]) => Promise<boolean>;
  /** Superadmin write to the universal layer. */
  setUniversalColors: (colors: string[]) => Promise<boolean>;
  /** Replace the brand's sparse channel-aspect override map. */
  setChannelAspect: (next: Record<string, AspectBucket>) => Promise<boolean>;
  /** Superadmin write of the complete universal aspect map. */
  setUniversalChannelAspect: (map: Record<string, AspectBucket>) => Promise<boolean>;
  /** Universal admin (SuperAdmin / CS): sparse safe-zone write ({channel: {placement: insets | null}}). */
  setUniversalSafeZones: (sparse: Record<string, Record<string, unknown>>) => Promise<boolean>;
  refresh: () => void;
}

const NULL_VALUE: BrandPaletteValue = {
  brandId: null,
  colors: null,
  ownColors: null,
  isOverride: false,
  universalColors: null,
  channelAspect: null,
  universalChannelAspect: null,
  universalSafeZones: null,
  isLoaded: false,
  saveColor: () => Promise.resolve(false),
  removeColor: () => Promise.resolve(false),
  setColors: () => Promise.resolve(false),
  setUniversalColors: () => Promise.resolve(false),
  setChannelAspect: () => Promise.resolve(false),
  setUniversalChannelAspect: () => Promise.resolve(false),
  setUniversalSafeZones: () => Promise.resolve(false),
  refresh: () => {},
};

const BrandPaletteContext = createContext<BrandPaletteValue>(NULL_VALUE);

export const useBrandPalette = (): BrandPaletteValue => useContext(BrandPaletteContext);

interface BrandPaletteProviderProps {
  brandId: number | null | undefined;
  children: ReactNode;
}

export const BrandPaletteProvider: React.FC<BrandPaletteProviderProps> = ({
  brandId,
  children,
}) => {
  const id = typeof brandId === 'number' && !Number.isNaN(brandId) ? brandId : null;
  const [entry, setEntry] = useState<PaletteEntry | null>(
    id !== null ? paletteCache.get(id) ?? null : null
  );

  useEffect(() => {
    if (id === null) {
      setEntry(null);
      return;
    }
    const listener = (next: PaletteEntry) =>
      setEntry({
        own: [...next.own],
        universal: [...next.universal],
        channelAspect: { ...next.channelAspect },
        universalChannelAspect: { ...next.universalChannelAspect },
        universalSafeZones: next.universalSafeZones,
      });
    let set = listeners.get(id);
    if (!set) {
      set = new Set();
      listeners.set(id, set);
    }
    set.add(listener);
    loadBrandPalette(id).then(listener);
    return () => {
      set!.delete(listener);
    };
  }, [id]);

  const saveColor = useCallback(
    (hex: string) => (id !== null ? saveBrandColorToPalette(id, hex) : Promise.resolve(false)),
    [id]
  );
  const removeColor = useCallback(
    (hex: string) => (id !== null ? removeBrandColorFromPalette(id, hex) : Promise.resolve(false)),
    [id]
  );
  const setColors = useCallback(
    (next: string[]) => (id !== null ? setBrandPalette(id, next) : Promise.resolve(false)),
    [id]
  );
  const setUniversalColors = useCallback(
    (next: string[]) => (id !== null ? setUniversalPalette(next) : Promise.resolve(false)),
    [id]
  );
  const setChannelAspect = useCallback(
    (next: Record<string, AspectBucket>) =>
      id !== null ? setBrandChannelAspect(id, next) : Promise.resolve(false),
    [id]
  );
  const setUniversalChannelAspect = useCallback(
    (map: Record<string, AspectBucket>) =>
      id !== null ? setUniversalChannelAspectMap(map) : Promise.resolve(false),
    [id]
  );
  const setUniversalSafeZones = useCallback(
    (sparse: Record<string, Record<string, unknown>>) =>
      id !== null ? setUniversalSafeZonesSparse(sparse) : Promise.resolve(false),
    [id]
  );
  const refresh = useCallback(() => {
    if (id !== null) loadBrandPalette(id, true);
  }, [id]);

  // Memoized so consumers' effects keyed on the arrays only refire when
  // the palette actually changes, not on every provider render.
  const value: BrandPaletteValue = useMemo(
    () =>
      id === null
        ? NULL_VALUE
        : {
            brandId: id,
            colors: entry ? resolveColors(entry) : null,
            ownColors: entry ? entry.own : null,
            isOverride: !!entry && entry.own.length > 0,
            universalColors: entry ? entry.universal : null,
            channelAspect: entry ? entry.channelAspect : null,
            universalChannelAspect: entry ? entry.universalChannelAspect : null,
            universalSafeZones: entry ? entry.universalSafeZones : null,
            isLoaded: entry !== null,
            saveColor,
            removeColor,
            setColors,
            setUniversalColors,
            setChannelAspect,
            setUniversalChannelAspect,
            setUniversalSafeZones,
            refresh,
          },
    [
      id,
      entry,
      saveColor,
      removeColor,
      setColors,
      setUniversalColors,
      setChannelAspect,
      setUniversalChannelAspect,
      setUniversalSafeZones,
      refresh,
    ]
  );

  return <BrandPaletteContext.Provider value={value}>{children}</BrandPaletteContext.Provider>;
};
